import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../actions/Button';
import CircularProgress from '../primitives/CircularProgress';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  /** Message body; the only required copy since most confirms are a single question. */
  message: React.ReactNode;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in error colors for destructive actions. */
  destructive?: boolean;
  /** Disables both buttons while the action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Shared replacement for window.confirm so confirmations match the app's dialogs. */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  message,
  title,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      <DialogTitle id="confirm-dialog-title">{title ?? t('confirm')}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-message">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy} intent="ghost">
          {cancelLabel ?? t('cancel')}
        </Button>
        <Button
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            if (busy) return;
            onConfirm();
          }}
          disabled={busy}
          intent={destructive ? 'danger' : 'primary'}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          autoFocus
        >
          {confirmLabel ?? t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
